/**
 * Statistics Utility Library
 * Provides functions for calculating standard scores, percentiles, and other psychometric metrics.
 */

// --- Core Math Functions ---

/**
 * Cumulative Distribution Function (CDF) for Standard Normal Distribution (Phi).
 * Returns the area under the curve from -infinity to z.
 * Input: z-score
 * Output: percentile (0-1)
 * Approximation: Abramowitz & Stegun (1964)
 */
const cdf = (z: number): number => {
    if (z < -6.5) return 0;
    if (z > 6.5) return 1;

    const b1 = 0.31938153;
    const b2 = -0.356563782;
    const b3 = 1.781477937;
    const b4 = -1.821255978;
    const b5 = 1.330274429;
    const p = 0.2316419;
    const c2 = 0.39894228;

    const a = Math.abs(z);
    const t = 1.0 / (1.0 + a * p);
    const b = c2 * Math.exp((-z * z) / 2.0);
    let n = ((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t;
    n = 1.0 - b * n;

    return z < 0 ? 1.0 - n : n;
};

/**
 * Inverse Cumulative Distribution Function (Probit).
 * Returns the z-score for a given percentile (0-1).
 * Input: p (0 < p < 1)
 * Output: z-score
 * Approximation: Beasley-Springer-Moro algorithm or simple Acklam approximation.
 * Using a simple approximation sufficient for psychometrics.
 */
const inverseCdf = (p: number): number => {
    if (p <= 0 || p >= 1) {
        // Handle edge cases or throw error. For our purpose, clamp loosely.
        if (p <= 0) return -4.0;
        if (p >= 1) return 4.0;
    }

    const a1 = -39.6968302866538;
    const a2 = 220.946098424521;
    const a3 = -275.928510446969;
    const a4 = 138.357751867269;
    const a5 = -30.6647980661472;
    const a6 = 2.50662827745924;

    const b1 = -54.4760987982241;
    const b2 = 161.585836858041;
    const b3 = -155.698979859887;
    const b4 = 66.8013118877197;
    const b5 = -13.2806815528857;

    const c1 = -0.00778489400243029;
    const c2 = -0.322396458041136;
    const c3 = -2.40075827716184;
    const c4 = -2.54973253934373;
    const c5 = 4.37466414146497;
    const c6 = 2.93816398269878;

    const d1 = 0.00778469570904146;
    const d2 = 0.32246712907004;
    const d3 = 2.445134137143;
    const d4 = 3.75440866190742;

    const p_low = 0.02425;
    const p_high = 1 - p_low;

    let q: number, r: number;

    if (p < p_low) {
        q = Math.sqrt(-2 * Math.log(p));
        return (
            (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
            ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
        );
    } else if (p <= p_high) {
        q = p - 0.5;
        r = q * q;
        return (
            ((((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q) /
            (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1)
        );
    } else {
        q = Math.sqrt(-2 * Math.log(1 - p));
        return (
            -(((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
            ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
        );
    }
};

// --- Helper Functions ---

export const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

// --- Basic Converters (Z -> X) ---

export const calculateZScore = (raw: number, mean: number, sd: number): number => {
    if (sd === 0) return 0;
    return (raw - mean) / sd;
};

export const calculateTScore = (z: number): number => 50 + 10 * z;

export const calculateStandardScore = (z: number, mean = 100, sd = 15): number => mean + sd * z; // IQ

export const calculateScaledScore = (z: number, mean = 10, sd = 3): number => mean + sd * z; // Wechsler Scaled (10/3)

export const calculatePercentile = (z: number): number => {
    const p = cdf(z);
    // Standard convention: 0-100, clamped.
    return clamp(p * 100, 0, 100);
};

export const calculateSten = (z: number): number => {
    // Sten: Mean 5.5, SD 2. Range 1-10.
    const val = 5.5 + 2 * z;
    return clamp(Math.round(val), 1, 10);
};

export const calculateStanine = (z: number): number => {
    // Stanine: Mean 5, SD 2. Range 1-9.
    const val = 5 + 2 * z;
    return clamp(Math.round(val), 1, 9);
};

// --- Inverse Converters (X -> Z) ---

export const tScoreToZ = (t: number): number => (t - 50) / 10;

export const iqToZ = (iq: number): number => (iq - 100) / 15;

export const scaledToZ = (s: number): number => (s - 10) / 3;

export const percentileToZ = (p: number): number => {
    if (p <= 0 || p >= 100) {
        // Edge cases logic could be handled here or by caller.
        // Returning extremas (+/- ~3.5-4)
        if (p <= 0) return -4.0;
        if (p >= 100) return 4.0;
    }
    return inverseCdf(p / 100);
};

export const stenToZ = (sten: number): number => (sten - 5.5) / 2;

export const stanineToZ = (stanine: number): number => (stanine - 5) / 2;
